/**
 * Workspace API Client – Phase 1 Infrastructure.
 *
 * Thin wrappers around fetchWithAuth for all workspace endpoints.
 * Does NOT touch the existing launcher/homepage system.
 *
 * Endpoints mirror: /api/v2/workspaces/...
 */
import { fetchWithAuth } from './backendApi';

const BASE = '/api/v2/workspaces';

// ---------------------------------------------------------------------------
// List workspaces for the calling user's tenant
// ---------------------------------------------------------------------------
export const listWorkspaces = (tenantId = null) => {
  const qs = tenantId ? `?tenantId=${encodeURIComponent(tenantId)}` : '';
  return fetchWithAuth(`${BASE}${qs}`, 'GET');
};

// ---------------------------------------------------------------------------
// Get a single workspace by ID
// ---------------------------------------------------------------------------
export const getWorkspace = (workspaceId, tenantId = null) => {
  const qs = tenantId ? `?tenantId=${encodeURIComponent(tenantId)}` : '';
  return fetchWithAuth(`${BASE}/${workspaceId}${qs}`, 'GET');
};

// ---------------------------------------------------------------------------
// Create a new workspace
// ---------------------------------------------------------------------------
export const createWorkspace = (payload) =>
  fetchWithAuth(BASE, 'POST', payload);

export const createStarterHome = () =>
  fetchWithAuth(`${BASE}/starter`, 'POST', {});

// ---------------------------------------------------------------------------
// Update an existing workspace
// ---------------------------------------------------------------------------
export const updateWorkspace = (workspaceId, payload) =>
  fetchWithAuth(`${BASE}/${workspaceId}`, 'PUT', payload);

// ---------------------------------------------------------------------------
// Archive (soft-delete) a workspace
// ---------------------------------------------------------------------------
export const archiveWorkspace = (workspaceId) =>
  fetchWithAuth(`${BASE}/${workspaceId}`, 'DELETE');

// ---------------------------------------------------------------------------
// Duplicate a workspace (copies metadata + latest published version as draft)
// ---------------------------------------------------------------------------
export const duplicateWorkspace = (workspaceId, name = null) =>
  fetchWithAuth(`${BASE}/${workspaceId}/duplicate`, 'POST', name ? { name } : {});

// ---------------------------------------------------------------------------
// List all versions for a workspace
// ---------------------------------------------------------------------------
export const listWorkspaceVersions = (workspaceId) =>
  fetchWithAuth(`${BASE}/${workspaceId}/versions`, 'GET');

// ---------------------------------------------------------------------------
// Assign workspace to user / role / tenantDefault
// ---------------------------------------------------------------------------
export const assignWorkspace = (workspaceId, { targetType, targetId }) =>
  fetchWithAuth(`${BASE}/${workspaceId}/assign`, 'POST', { targetType, targetId });

// ---------------------------------------------------------------------------
// Resolve the current workspace for the authenticated user.
// Returns: { source: 'workspace'|'legacy-launcher', workspace: {...}|null, fallback: bool }
// When fallback is true the caller MUST use the existing launcher system.
// ---------------------------------------------------------------------------
export const resolveCurrentWorkspace = () =>
  fetchWithAuth(`${BASE}/resolve/current`, 'GET');

// ===========================================================================
// Phase 2 – Lifecycle
// ===========================================================================

// Get the current published version of a workspace
export const getPublishedVersion = (workspaceId) =>
  fetchWithAuth(`${BASE}/${workspaceId}/published`, 'GET');

// Get the current draft version of a workspace
export const getDraftVersion = (workspaceId) =>
  fetchWithAuth(`${BASE}/${workspaceId}/draft`, 'GET');

// Update the layoutSchema of the current draft (creates draft if none exists)
// RBAC: tenant_admin / admin / superadmin only
export const updateWorkspaceDraft = (workspaceId, layoutSchema, baseRevision = undefined) =>
  fetchWithAuth(`${BASE}/${workspaceId}/draft`, 'PATCH', {
    layoutSchema,
    ...(baseRevision == null ? {} : { baseRevision }),
  });

// Get full version history for a workspace
export const getVersionHistory = (workspaceId) =>
  fetchWithAuth(`${BASE}/${workspaceId}/history`, 'GET');

// Publish the current draft (atomic)
export const publishWorkspace = (workspaceId) =>
  fetchWithAuth(`${BASE}/${workspaceId}/publish`, 'POST', {});

// Rollback: create new draft from a historical version
export const rollbackWorkspace = (workspaceId, versionId) =>
  fetchWithAuth(`${BASE}/${workspaceId}/rollback/${versionId}`, 'POST', {});

// ===========================================================================
// Phase 5 – Launcher Import
// ===========================================================================

// List users in a tenant with their launcher layout info (hasLayout, iconCount)
// SA: pass tenantId param to query cross-tenant
export const listImportSources = ({ tenantId, q, limit } = {}) => {
  const params = new URLSearchParams();
  if (tenantId) params.set('tenantId', tenantId);
  if (q)        params.set('q', q);
  if (limit)    params.set('limit', String(limit));
  const qs = params.toString() ? `?${params}` : '';
  return fetchWithAuth(`${BASE}/import/sources${qs}`, 'GET');
};

// Get a specific user's launcher layout for preview
export const getImportSource = (userId, { tenantId } = {}) => {
  const qs = tenantId ? `?tenantId=${encodeURIComponent(tenantId)}` : '';
  return fetchWithAuth(`${BASE}/import/source/${userId}${qs}`, 'GET');
};

// Create a workspace from a legacy launcher layout
// payload: { name, sourceUserId, sourceTenantId, layoutData, targetTenantId? }
export const createWorkspaceFromImport = (payload) =>
  fetchWithAuth(`${BASE}/import/create`, 'POST', payload);

// Create a new empty (or seeded) draft version
export const createDraft = (workspaceId, layoutSchema = null) =>
  fetchWithAuth(`${BASE}/${workspaceId}/create-draft`, 'POST', layoutSchema ? { layoutSchema } : {});

// ===========================================================================
// Phase 2 – Assignment management
// ===========================================================================

// List all assignments for a workspace
export const listAssignments = (workspaceId) =>
  fetchWithAuth(`${BASE}/${workspaceId}/assignments`, 'GET');

// Delete an assignment by its ID
export const deleteAssignment = (assignmentId) =>
  fetchWithAuth(`${BASE}/assignments/${assignmentId}`, 'DELETE');
